/**
 * Flatlogic Dashboards (https://flatlogic.com/admin-dashboards)
 *
 * Copyright © 2015-present Flatlogic, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import React, { useState } from 'react';
import cx from 'classnames';
import { useLocation } from 'react-router-dom';
import { Switch, Route, Redirect, withRouter } from 'react-router';

import s from './Layout.module.scss';
import Header from '../Header';
import Footer from '../Footer';
import Sidebar from '../Sidebar';
import { CityProvider } from '../../contexts/CityContext';
import { DateFilterProvider } from '../../contexts/DateFilterContext';
import { ChatProvider, useChat } from '../../contexts/ChatContext';
import { OpsMapStateProvider } from '../../contexts/OpsMapStateContext';
import { MapDataProvider } from '../../contexts/MapDataContext';
import { GoogleMapsProvider } from '../../context/GoogleMapsProvider';
import ConversationDetailsModal from '../../pages/conversations/ConversationDetailsModal';
import StartConversationModal from '../../pages/customers/StartConversationModal';
import MinimizedChatBubble from '../../pages/conversations/MinimizedChatBubble';

import OperationsDashboard from '../../pages/dashboard/OperationsDashboard'
import Drivers from '../../pages/drivers/Drivers'
import Vehicles from '../../pages/vehicles/Vehicles'
import Trips from '../../pages/trips/Trips'
import UnlockedBookings from '../../pages/trips/list/UnlockedBookings'
import Buttons from '../../pages/buttons'
import Charts from '../../pages/charts'
import Maps from '../../pages/google'
import NotFound from '../../pages/notFound'
import Icons from '../../pages/icons'
import Customers from '../../pages/customers/Customers'
import NewBooking from '../../pages/customers/NewBooking'
import Conversations from '../../pages/conversations/Conversations'
import Bookings from '../../pages/bookings/Bookings'
import Leads from '../../pages/leads/Leads'
import CallManagerPage from '../../pages/call-manager/CallManagerPage'
import PaymentCenterPage from '../../pages/payments/PaymentCenterPage'
import Notifications from '../../pages/notifications'
import Posts from '../../pages/posts'
import Profile from '../../pages/profile'
import Privacy from '../../pages/privacy'


// Assignment Management Features from Frontend1
import AssignmentReference from '../../pages/assignments/AssignmentReference'
import LockedAssignments from '../../pages/assignments/LockedAssignments'
import VehicleDetails from '../../pages/dispatch/VehicleDetails'
import VehicleLeads from '../../pages/dispatch/VehicleLeads'
import EscalationDashboard from '../../pages/operations/EscalationDashboard'
import OpsMapDashboard from '../../pages/ops/OpsMapDashboard'

// Support Tickets (Ops)
import SupportTicketsPage from '../../pages/support/SupportTicketsPage'

// OPS Field Registration
import OpsRegister from '../../pages/ops/OpsRegister'

// Knowledge Base (Phase 2)
import KnowledgeGaps from '../../pages/knowledge/KnowledgeGaps'
import KnowledgeDocuments from '../../pages/knowledge/KnowledgeDocuments'
import BookingJournal from '../../pages/knowledge/BookingJournal'

// ─── Inner Layout (functional – can use hooks) ───────────────────────────────
const LayoutInner = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { chatState, minimizeChat, closeChat } = useChat();
  const location = useLocation();
  const isNewBookingActive = location.pathname.startsWith('/app/customers/new-booking') || location.pathname.startsWith('/app/customers/modify-booking');

  return (
    <div className={s.root}>
      <Sidebar sidebarOpen={sidebarOpen} />
      <div className={cx(s.wrap, { [s.sidebarOpen]: sidebarOpen })}>
        <Header sidebarToggle={() => setSidebarOpen(prev => !prev)} />

        <main className={s.content}>
          <div style={{ display: isNewBookingActive ? 'block' : 'none', height: '100%' }}>
            <NewBooking key={`${location.pathname}${location.search}`} />
          </div>
          <Switch>
            <Route path="/app/main" exact render={() => <Redirect to="/app/operations-dashboard" />} />
            <Route path="/app/drivers" component={Drivers} />
            <Route path="/app/vehicles" component={Vehicles} />
            <Route path="/app/customers" exact component={Customers} />
            <Route path="/app/customers/new-booking" exact render={() => null} />
            <Route path="/app/customers/modify-booking" exact render={() => null} />
            <Route path="/app/conversations" exact component={Conversations} />
            <Route path="/app/leads" exact component={Leads} />
            <Route path="/app/call-manager" exact component={CallManagerPage} />
            <Route path="/app/bookings" exact component={Bookings} />
            <Route path="/app/payment-center" exact component={PaymentCenterPage} />
            <Route path="/app/unlocked-bookings" exact component={UnlockedBookings} />
            <Route path="/app/trips" component={Trips} />
            <Route path="/app/call-logs" exact render={() => <Redirect to="/app/dispatch/vehicles?tab=call-logs" />} />

            {/* Assignment Management Routes */}
            <Route path="/app/assignments/reference" exact component={AssignmentReference} />
            <Route path="/app/assignments/locked" exact component={LockedAssignments} />
            <Route path="/app/dispatch/vehicles" exact component={VehicleDetails} />
            <Route path="/app/vehicle-leads" exact component={VehicleLeads} />
            <Route path="/app/operations/escalation" exact component={EscalationDashboard} />

            {/* Ops Map Dashboard */}
            <Route path="/app/ops/map" exact component={OpsMapDashboard} />

            {/* OPS Field Registration */}
            <Route path="/app/ops/register" exact component={OpsRegister} />

            {/* Logistics Operations Dashboard */}
            <Route path="/app/operations-dashboard" exact component={OperationsDashboard} />

            {/* Support Tickets */}
            <Route path="/app/support" exact component={SupportTicketsPage} />

            {/* Knowledge Base (Phase 2) */}
            <Route path="/app/knowledge/gaps" exact component={KnowledgeGaps} />
            <Route path="/app/knowledge/documents" exact component={KnowledgeDocuments} />
            <Route path="/app/knowledge/booking-journal" exact component={BookingJournal} />

            <Route path="/app/posts" component={Posts} />
            <Route path="/app/privacy" exact component={Privacy} />
            <Route path="/app/profile" exact component={Profile} />
            <Route path="/app/notifications" exact component={Notifications} />
            <Route path="/app/components/buttons" exact component={Buttons} />
            <Route path="/app/components/charts" exact component={Charts} />
            <Route path="/app/components/icons" exact component={Icons} />
            <Route path="/app/components/maps" exact component={Maps} />
            <Route component={NotFound} />
          </Switch>
        </main>

        <Footer />
      </div>

      {sidebarOpen && (
        <div className={s.sidebarBackdrop} onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Global Chat Modals ─────────────────────────────────────────────
          Rendered at the layout level so they persist across route changes.
          When minimized, the modal unmounts and MinimizedChatBubble appears.
      ────────────────────────────────────────────────────────────────────── */}
      {(!chatState.modalType || chatState.modalType === 'conversation') && (
        <ConversationDetailsModal
          isOpen={chatState.isOpen && chatState.modalType === 'conversation'}
          isMinimized={chatState.isMinimized && chatState.modalType === 'conversation'}
          toggle={closeChat}
          onMinimize={minimizeChat}
          customerId={chatState.customerId}
          conversationId={chatState.conversationId}
          userId={chatState.userId}
          isOngoingSession={chatState.isOngoingSession}
        />
      )}
      {chatState.modalType === 'startConversation' && (
        <StartConversationModal
          isOpen={chatState.isOpen}
          isMinimized={chatState.isMinimized}
          toggle={closeChat}
          onMinimize={minimizeChat}
          customerId={chatState.customerId}
          userId={chatState.userId}
          isJoining={chatState.isJoining}
        />
      )}

      {/* Floating minimized bubble – persists across navigation */}
      <MinimizedChatBubble />
    </div>
  );
};

// ─── Layout class wrapper (provides context providers) ───────────────────────
class Layout extends React.Component {
  render() {
    return (
      <CityProvider>
        <DateFilterProvider>
          <GoogleMapsProvider>
            <MapDataProvider>
              <ChatProvider>
                <OpsMapStateProvider>
                  <LayoutInner />
                </OpsMapStateProvider>
              </ChatProvider>
            </MapDataProvider>
          </GoogleMapsProvider>
        </DateFilterProvider>
      </CityProvider>
    );
  }
}

export default withRouter(Layout);
